/**
 * Operations-dashboard hero: the desktop left-hand stat-card stack (new
 * alarms, active alarms, resolved alarms) — each card doubles as a filter
 * toggle onto the alarms tab's status filter via `onClick`.
 *
 * Contains:
 * - OperationsStatCardsLeft — default export
 */

import BuildIcon from '@mui/icons-material/Build';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import Stack from '@mui/material/Stack';
import React from 'react';

import StatCard from '@sophocles/components/StatCard';
import { GRADIENTS } from '@sophocles/features/dashboards/chipStyles';
import { formatTrendLabel, formatTrendPrevious } from '@sophocles/features/dashboards/opsHelpers';

export default function OperationsStatCardsLeft({
  t,
  data,
  loading,
  hoursLabel,
  newAlarmsCount,
  alarmSitesChange,
  resolvedCount,
  clearedChange,
  activeTab,
  selectedAlarms,
  structureFilter,
  alarmStatus,
  setActiveTab,
  setSelectedAlarms,
  setStructureFilter,
  setAlarmStatus,
  showTrend,
}) {
  return (
    <Stack
      spacing={0.75}
      sx={{
        width: { lg: 240 },
        flexShrink: 0,
        justifyContent: 'center',
        flexDirection: { xs: 'row', lg: 'column' },
        flexWrap: { xs: 'wrap', lg: 'nowrap' },
        display: { xs: 'none', lg: 'flex' },
        minHeight: { lg: 320 },
      }}
      useFlexGap
    >
      <StatCard
        title={`${t('operationsDashboard.newAlarms', { defaultValue: 'New Alarms' })} (${hoursLabel})`}
        value={newAlarmsCount}
        color="warning.main"
        gradient={GRADIENTS.warning}
        loading={loading}
        icon={<NotificationsActiveIcon />}
        change={showTrend ? alarmSitesChange : null}
        previousValue={showTrend ? data?.alarm_summary?.previous_total : null}
        changeLabel={showTrend ? formatTrendLabel(alarmSitesChange, t) : undefined}
        previousLabel={showTrend ? formatTrendPrevious(data?.alarm_summary?.previous_total, t) : undefined}
        onClick={() => {
          setAlarmStatus('all');
          if (activeTab === 'alarms' && selectedAlarms.length === 0 && !structureFilter) {
            return;
          }
          setActiveTab('alarms');
          setSelectedAlarms([]);
          setStructureFilter(null);
        }}
        active={activeTab === 'alarms' && alarmStatus === 'all' && selectedAlarms.length === 0 && !structureFilter}
        tooltip={t('operationsDashboard.tooltipNewAlarms', {
          defaultValue: 'New alarms raised during the selected time window.',
        })}
      />
      <StatCard
        title={t('operationsDashboard.activeAlarms', { defaultValue: 'Unresolved Alarms (all)' })}
        value={data?.active_alarms ?? '-'}
        color="error.main"
        gradient={GRADIENTS.error}
        loading={loading}
        icon={<ErrorOutlineIcon />}
        onClick={() => {
          setActiveTab('alarms');
          setSelectedAlarms([]);
          setStructureFilter(null);
          setAlarmStatus(alarmStatus === 'active' ? 'all' : 'active');
        }}
        active={activeTab === 'alarms' && alarmStatus === 'active'}
        tooltip={t('operationsDashboard.tooltipActiveAlarms', {
          defaultValue:
            'The point-in-time open set: an alarm raised at any time that is still active, not scoped to the selected time window.',
        })}
      />
      <StatCard
        title={`${t('operationsDashboard.resolvedAlarms', { defaultValue: 'Resolved Alarms' })} (${hoursLabel})`}
        value={resolvedCount}
        color="success.main"
        gradient={GRADIENTS.success}
        loading={loading}
        icon={<BuildIcon />}
        change={showTrend ? clearedChange : null}
        previousValue={showTrend ? data?.period_stats?.cleared?.previous : null}
        changeLabel={showTrend ? formatTrendLabel(clearedChange, t) : undefined}
        previousLabel={showTrend ? formatTrendPrevious(data?.period_stats?.cleared?.previous, t) : undefined}
        invertChange
        onClick={() => {
          setActiveTab('alarms');
          setSelectedAlarms([]);
          setStructureFilter(null);
          setAlarmStatus(alarmStatus === 'resolved' ? 'all' : 'resolved');
        }}
        active={activeTab === 'alarms' && alarmStatus === 'resolved'}
        tooltip={t('operationsDashboard.tooltipResolvedAlarms', {
          defaultValue: 'Alarms cleared during the selected time window.',
        })}
        subStat={resolvedCount > newAlarmsCount ? '✓' : undefined}
        subStatColor={resolvedCount > newAlarmsCount ? 'success.main' : undefined}
      />
    </Stack>
  );
}
